import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { ModalForm, type FormField } from '@/components/ui/modal-form';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useLocations, useCreateLocation, useUpdateLocation, useDeleteLocation } from '@/hooks/useLocations';
import { useToast } from '@/hooks/useToast';
import type { Location } from '@/lib/schemas';
import {
  Plus,
  Search,
  MapPin,
  Building2,
  Warehouse,
  Home,
  FolderTree,
  AlertCircle,
} from 'lucide-react';

const typeIcons = {
  warehouse: Warehouse,
  ward: Home,
  department: Building2,
  storage: FolderTree,
};

const typeColors = {
  warehouse: 'default',
  ward: 'info',
  department: 'warning',
  storage: 'default',
} as const;

export default function LocationsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [deletingLocation, setDeletingLocation] = useState<Location | null>(null);

  const { data: locations = [], isLoading, error } = useLocations();
  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();
  const deleteMutation = useDeleteLocation();
  const toast = useToast();

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'ward', label: 'Ward' },
    { value: 'department', label: 'Department' },
    { value: 'storage', label: 'Storage' },
  ];

  const filteredLocations = useMemo(() => {
    return locations.filter(
      (loc) =>
        (loc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        loc.code.toLowerCase().includes(searchQuery.toLowerCase())) &&
        (typeFilter === 'all' || loc.location_type === typeFilter)
    );
  }, [locations, searchQuery, typeFilter]);

  const handleCreate = async (data: Partial<Location>) => {
    try {
      await createMutation.mutateAsync(data as Omit<Location, 'location_id' | 'created_at' | 'updated_at'>);
      setIsCreateOpen(false);
      toast.success('Location created', 'Location has been created successfully.');
    } catch (error: any) {
      toast.error('Failed to create location', error.message || 'An unknown error occurred');
    }
  };

  const handleUpdate = async (data: Partial<Location>) => {
    if (editingLocation && editingLocation.location_id) {
      try {
        await updateMutation.mutateAsync({
          id: editingLocation.location_id,
          data: data as Partial<Omit<Location, 'location_id' | 'created_at'>>,
        });
        setEditingLocation(null);
        toast.success('Location updated', 'Location has been updated successfully.');
      } catch (error: any) {
        toast.error('Failed to update location', error.message || 'An unknown error occurred');
      }
    }
  };

  const handleDelete = async () => {
    if (deletingLocation && deletingLocation.location_id) {
      try {
        await deleteMutation.mutateAsync(deletingLocation.location_id);
        setDeletingLocation(null);
        toast.success('Location deleted', 'Location has been deleted successfully.');
      } catch (error: any) {
        toast.error('Failed to delete location', error.message || 'An unknown error occurred');
      }
    }
  };

  const locationFields = [
    {
      name: 'hospital_id',
      label: 'Hospital ID',
      type: 'number' as const,
      required: true,
      placeholder: 'Enter hospital ID',
    },
    {
      name: 'name',
      label: 'Location Name',
      type: 'text' as const,
      required: true,
      placeholder: 'Enter location name',
    },
    {
      name: 'code',
      label: 'Location Code',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., WH-MAIN',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea' as const,
      placeholder: 'Enter description (optional)',
    },
    {
      name: 'location_type',
      label: 'Location Type',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'warehouse', label: 'Warehouse' },
        { value: 'ward', label: 'Ward' },
        { value: 'department', label: 'Department' },
        { value: 'storage', label: 'Storage' },
      ],
      helperText: 'Select the type of location',
    },
    {
      name: 'is_active',
      label: 'Active',
      type: 'checkbox' as const,
      helperText: 'Whether this location is active',
    },
  ] as const satisfies FormField[];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Locations
          </h1>
          <p className="text-text-secondary mt-1">
            Manage warehouse, ward, and department inventory locations
          </p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Location
        </Button>
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="p-4 rounded-lg bg-red-50 border-2 border-red-200 flex items-start gap-3"
          role="alert"
          aria-live="assertive"
        >
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-red-900">Error loading locations</p>
            <p className="text-sm text-red-800 mt-1">{error.message}</p>
          </div>
        </div>
      )}

      {/* Stats Skeleton */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="h-4 w-20 bg-border rounded animate-pulse mb-2" />
                    <div className="h-8 w-12 bg-border rounded animate-pulse" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
              <Input
                placeholder="Search locations by name or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                aria-label="Search locations"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table view */}
      {isLoading ? (
        <TableSkeleton rows={10} columns={4} />
      ) : filteredLocations.length > 0 ? (
        <DataTable
          columns={[
            {
              key: 'name',
              header: 'Name',
              render: (location: Location) => (
                <div className="flex items-center gap-3">
                  {(() => {
                    const TypeIcon = typeIcons[location.location_type as keyof typeof typeIcons];
                    const colorVariant = typeColors[location.location_type as keyof typeof typeColors];
                    return (
                      <div className={`p-2 rounded-lg bg-${colorVariant}/10`}>
                        <TypeIcon className={`h-5 w-5 text-${colorVariant}`} />
                      </div>
                    );
                  })()}
                  <div>
                    <div className="font-semibold text-text-primary">{location.name}</div>
                    <div className="text-sm text-text-muted">{location.code}</div>
                  </div>
                </div>
              ),
            },
            {
              key: 'location_type',
              header: 'Type',
              render: (location: Location) => (
                <Badge variant={typeColors[location.location_type as keyof typeof typeColors] as any} className="capitalize">
                  {location.location_type}
                </Badge>
              ),
            },
            {
              key: 'hospital_id',
              header: 'Hospital ID',
              render: (location: Location) => (
                <span className="text-text-secondary">#{location.hospital_id}</span>
              ),
            },
            {
              key: 'is_active',
              header: 'Status',
              render: (location: Location) => (
                <Badge variant={location.is_active ? 'success' : 'error'}>
                  {location.is_active ? 'Active' : 'Inactive'}
                </Badge>
              ),
            },
          ]}
          data={filteredLocations}
          loading={false}
          onEdit={setEditingLocation}
          onDelete={setDeletingLocation}
          getItemId={(loc) => loc.location_id!}
        />
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <MapPin className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No locations found</p>
            <p className="text-sm text-text-muted mt-1">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Create your first location to get started'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {filteredLocations.length === 0 && !isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <MapPin className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No locations found</p>
            <p className="text-sm text-text-muted mt-1">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Create your first location to get started'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create Modal */}
      <ModalForm<Location>
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        title="Add New Location"
        description="Create a new location for your hospital inventory."
        fields={locationFields}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      {/* Edit Modal */}
      <ModalForm<Location>
        open={!!editingLocation}
        onOpenChange={(open) => !open && setEditingLocation(null)}
        title="Edit Location"
        description={`Editing: ${editingLocation?.name}`}
        fields={locationFields}
        initialData={editingLocation || undefined}
        onSubmit={handleUpdate}
        isLoading={updateMutation.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deletingLocation}
        onOpenChange={(open) => !open && setDeletingLocation(null)}
        title="Delete Location"
        description={`Are you sure you want to delete "${deletingLocation?.name}"? This action cannot be undone and may affect inventory assignments.`}
        onConfirm={handleDelete}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}
